import logger from '../utils/logger';
/**
 * HGIPayments Component
 * -------------------
 * Automatic payments page. When an appointment is selected (or handed off from the
 * Scheduling checkout flow), the billable service lines are generated automatically
 * from the orders performed for that appointment:
 *   - Screening appointments  -> one flat package-price line (+ free consultation)
 *   - Diagnostic appointments -> one line per imaging order, priced by modality,
 *                                plus a Contrast line for any order with contrast.
 * A clinic-membership 10% discount is detected and applied to imaging lines
 * automatically. Total paid is aggregated from all payment sources (Stripe/cash/check)
 * rather than a stored running total.
 *
 * Phase 1: automatic line generation, aggregate "amount paid", print Bill/Invoice/Receipt,
 *          group printing. (Discount codes, manual add/remove, multi-appointment
 *          collection, and the transactions view arrive in Phase 2.)
 */

import { React, useState, useEffect, useContext } from "react";
import "../style/HGIPayments.css";
import { useNavigate, useLocation } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { getUserId, formatAppointmentDate, formatAppointmentTimeRange, computeBalanceDue, fetchWithTimeout } from "../utils/utils";
import {
  generateBill,
  generateReceipt,
  generateMailingInvoice
} from './ItemizedPayments';
import { GlobalStateContext } from "../utils/GlobalStateContext";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Home, UserCheck, Wallet, ReceiptText, CalendarClock, ArrowRight, UserPlus, ShieldAlert } from 'lucide-react';
import PageTopBar from './customComponents/PageTopBar';
import InsuranceCaptureModal from './customComponents/InsuranceCaptureModal';

const HGIPayments = () => {
  const { currentPatient, currentUser } = useContext(GlobalStateContext);
  // Appointment id handed off from the Scheduling checkout flow, to auto-select below.
  const location = useLocation();
  const preselectAppointmentId = location.state?.appointmentId;

  const [schedule, setSchedule] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [id, setId] = useState(null);                 // ris_payments_id (hgi_payments.id)
  const [patientName, setPatientName] = useState(null);
  const [email, setEmail] = useState(null);
  const [patientAddress, setPatientAddress] = useState(null);

  // Automatically derived charges for the selected appointment.
  const [charges, setCharges] = useState(null);       // { lineItems, discounts, subtotal, discountTotal, total, membership, dateOfService, visit_type }
  const [chargesLoading, setChargesLoading] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);      // aggregate across all payment sources

  // Recorded payments (for itemized receipts).
  const [billData, setBillData] = useState({ payments: [], cashPayments: [], checkPayments: [] });

  // Group printing.
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroupAppts, setSelectedGroupAppts] = useState([]);
  // Which group action is running ('collect' | 'bill' | 'receipt' | 'invoice' | null). Only the
  // clicked button shows progress — a single shared flag made all four read "Loading…", so a
  // stall in one looked like the whole modal had hung.
  const [groupBusy, setGroupBusy] = useState(null);
  const groupLoading = groupBusy !== null;

  // Modify services (manual add / remove).
  const [addServiceKey, setAddServiceKey] = useState('');
  const [addServiceQty, setAddServiceQty] = useState(1);
  const [removeModal, setRemoveModal] = useState({ open: false, line: null });
  const [removeReason, setRemoveReason] = useState('');

  // DB-backed add-on service catalog (replaces the hardcoded ADDON_SERVICES list).
  const [serviceCatalog, setServiceCatalog] = useState([]);

  // Open charge (custom amount / set total to a given figure).
  const [customDesc, setCustomDesc] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [customQty, setCustomQty] = useState(1);
  const [setTotalTarget, setSetTotalTarget] = useState('');
  const [openChargeBusy, setOpenChargeBusy] = useState(false);

  // Manage Services modal (admin add/edit of the catalog).
  const [manageOpen, setManageOpen] = useState(false);
  const [manageRows, setManageRows] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [newSvc, setNewSvc] = useState({ service_key: '', description: '', unit_price: '' });
  const [newSvcBusy, setNewSvcBusy] = useState(false);

  // Discount codes.
  const [discountCode, setDiscountCode] = useState('');
  const [selectedLineKeys, setSelectedLineKeys] = useState([]);
  const [discountBusy, setDiscountBusy] = useState(false);

  // Transaction history.
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txScope, setTxScope] = useState('appointment'); // 'appointment' | 'all'

  // Retroactive card payment.
  const [retroModalOpen, setRetroModalOpen] = useState(false);
  const [retroTransactionId, setRetroTransactionId] = useState('');
  const [retroAmount, setRetroAmount] = useState('');
  const [retroDate, setRetroDate] = useState(new Date().toISOString().split('T')[0]);
  const [retroDescription, setRetroDescription] = useState('');
  const [retroLoading, setRetroLoading] = useState(false);

  // Insurance billing + copay. When the appointment is billed to insurance, the patient is
  // responsible only for the copay (entered here) rather than the full visit charges.
  const [billInsurance, setBillInsurance] = useState(false);
  const [copayInput, setCopayInput] = useState('');
  const [copaySaving, setCopaySaving] = useState(false);
  const [insuranceToggling, setInsuranceToggling] = useState(false);

  // Free-text notes on the payment header (e.g. context on a discount or arrangement).
  const [notesInput, setNotesInput] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);

  // Whether the patient has any active (non-archived) insurance on file. An appointment
  // can't truly be billed to insurance until a policy exists, so we surface a prompt to
  // add one (scan or manual) when billing is set to insurance but none is on record.
  const [activeInsuranceCount, setActiveInsuranceCount] = useState(null); // null = not yet loaded
  const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_URL;
  const user = getUserId(currentUser);
  const navigate = useNavigate();

  const dateOfService = (charges?.dateOfService || selectedAppointment?.appointment_start || '').split('T')[0] || null;
  // When billing insurance the patient owes only the copay; otherwise the full visit total.
  // The copay must be entered explicitly (even $0) — an empty field counts as "not entered",
  // never silently treated as $0, so staff can't accidentally skip collecting a copay.
  const copayEntered = copayInput !== '' && copayInput !== null && copayInput !== undefined;
  const copayRequiredMissing = billInsurance && !copayEntered;
  const copayAmount = Number(copayInput) || 0;
  const patientResponsible = billInsurance ? copayAmount : (charges?.total || 0);
  const balanceDue = billInsurance
    ? Math.max(0, Number((copayAmount - totalPaid).toFixed(2)))
    : computeBalanceDue(charges, totalPaid);

  // Transactions filtered to the selected appointment, or all of the patient's.
  const visibleTransactions = (txScope === 'appointment' && selectedAppointment)
    ? transactions.filter((t) => Number(t.schedule_id) === Number(selectedAppointment.id))
    : transactions;

  // Build the printable charge list (description/qty/unitPrice) expected by ItemizedPayments.
  // Discounts are represented as a single qty:1 line with a negative unit price.
  const buildPrintableCharges = (data = charges) => {
    if (!data) return [];
    const lines = (data.lineItems || [])
      .filter((l) => l.qty >= 1)
      .map((l) => ({
        description: l.description, qty: l.qty, unitPrice: l.unitPrice,
        cptCode: l.cptCode || null, orderingProvider: l.orderingProvider || null, referringProvider: l.referringProvider || null,
      }));
    const discountLines = (data.discounts || []).map((d) => ({
      description: d.description,
      qty: 1,
      unitPrice: d.amount,
    }));
    const printable = [...lines, ...discountLines];
    // For an insurance visit the services above are billed to the payer; the patient owes only
    // the copay. Net the patient-responsible total down to the copay with an adjustment line so
    // every printed document (bill / invoice / receipt) totals the copay.
    if (billInsurance) {
      const adjustment = Number((copayAmount - Number(data.total || 0)).toFixed(2));
      if (adjustment !== 0) {
        printable.push({
          description: 'Less: Billed to insurance (patient owes copay)',
          qty: 1,
          unitPrice: adjustment,
        });
      }
    }
    return printable;
  };

  const chargeData = {
    responsibleName: patientName,
    dateOfService,
    charges: buildPrintableCharges(),
  };

  /**
   * Fetches the radiology/screening appointments for the current patient.
   */
  const fetchAppointments = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/payments/patientAppointments?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length === 0) {
          toast.info("No appointments found for this patient");
          return;
        }
        setPatientName(data[0].first_name + ' ' + data[0].last_name);
        setSchedule(data);
        setEmail(data[0].email);
      } else {
        toast.error("Failed to fetch appointment information");
      }
    } catch (error) {
      logger.error("Error fetching data", error);
      toast.error("Error loading appointments. Please try again.");
    }
  };

  const fetchPatientAddress = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatient?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const p = data[0];
          setPatientAddress({
            address: `${p.street_number || ''} ${p.street_name || ''}`.trim(),
            aptNumber: p.apt_number || '',
            city: p.city || '',
            state: p.state || '',
            zip: p.zip ? String(p.zip) : ''
          });
        }
      } else {
        logger.error('Failed to fetch patient address');
      }
    } catch (error) {
      logger.error('Error fetching patient address:', error);
    }
  };

  /**
   * Loads the patient's insurance on file (same endpoint as the patient profile) and
   * records how many active (non-archived) policies exist. Used to warn when an
   * appointment is billed to insurance but the patient has no policy on record.
   */
  const fetchInsuranceOnFile = async () => {
    if (!currentPatient?.ris_patients_id) return;
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        const active = Array.isArray(data) ? data.filter((i) => !i.archived).length : 0;
        setActiveInsuranceCount(active);
      } else {
        setActiveInsuranceCount(0);
      }
    } catch (error) {
      logger.error('Error fetching insurance on file', error);
      setActiveInsuranceCount(0);
    }
  };

  /**
   * Loads the DB-backed add-on service catalog (active services) for the "Add service" control.
   */
  const fetchServiceCatalog = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/payments/serviceCatalog?user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        setServiceCatalog(Array.isArray(data) ? data : []);
      } else {
        logger.error('Failed to fetch service catalog');
      }
    } catch (error) {
      logger.error('Error fetching service catalog:', error);
    }
  };

  /**
   * Ensures a payment header row (hgi_payments) exists for the appointment so that
   * collected payments (charges_ris/cash/check) have a foreign-key target. Returns its id.
   */
  const ensurePaymentRecord = async (appt) => {
    try {
      const response = await fetchWithTimeout(
        `${BASE_URL}/payments/getHGIPayment?schedule_id=${appt.id}&ris_patients_id=${currentPatient.ris_patients_id}&patient_name=${patientName}&user=${user}&type=${currentUser.type}`
      );
      if (!response.ok) {
        logger.error('getHGIPayment failed', response.status);
        toast.error(`Couldn't load the payment record for appointment #${appt.id}`);
        return null;
      }

      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data[0].id;
      }

      // No record yet — create one.
      const postRes = await fetchWithTimeout(`${BASE_URL}/payments/postHGIPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ris_patients_id: currentPatient.ris_patients_id,
          patients_name: patientName,
          schedule_id: appt.id,
          user,
          type: currentUser.type
        }),
      });
      if (postRes.ok) {
        return await postRes.json();
      }

      // Surface the server's reason instead of failing silently — a schema/constraint problem
      // here used to look like "no balance due" further up the flow.
      const detail = await postRes.json().catch(() => ({}));
      logger.error('postHGIPayment failed', postRes.status, detail);
      toast.error(detail.detail || detail.error || `Couldn't create a payment record for appointment #${appt.id}`);
      return null;
    } catch (error) {
      logger.error("Error ensuring payment record", error);
      toast.error(error?.timeout
        ? `Timed out preparing appointment #${appt.id} — check your connection and try again`
        : `Couldn't load the payment record for appointment #${appt.id}`);
      return null;
    }
  };

  /**
   * Read-only lookup of an existing payment header id for an appointment (no creation).
   */
  const getExistingPaymentId = async (apptId) => {
    try {
      const r = await fetchWithTimeout(
        `${BASE_URL}/payments/getHGIPayment?schedule_id=${apptId}&ris_patients_id=${currentPatient.ris_patients_id}&patient_name=${patientName}&user=${user}&type=${currentUser.type}`
      );
      if (!r.ok) return null;
      const data = await r.json();
      return Array.isArray(data) && data.length > 0 ? data[0].id : null;
    } catch {
      return null;
    }
  };

  const fetchVisitCharges = async (scheduleId) => {
    const response = await fetchWithTimeout(
      `${BASE_URL}/payments/visitCharges?schedule_id=${scheduleId}&ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
    );
    if (!response.ok) {
      toast.error("Failed to load visit charges");
      return null;
    }
    return response.json();
  };

  const fetchAmountPaid = async (paymentsId) => {
    if (!paymentsId) return 0;
    try {
      const response = await fetchWithTimeout(
        `${BASE_URL}/payments/amountPaid?payments_id=${paymentsId}&user=${user}&type=${currentUser.type}`
      );
      if (!response.ok) return 0;
      const data = await response.json();
      return Number(data.totalPaid) || 0;
    } catch (error) {
      logger.error("Error fetching amount paid", error);
      return 0;
    }
  };

  const fetchPaymentCharges = async (paymentsId) => {
    if (!paymentsId) return null;
    try {
      const response = await fetchWithTimeout(
        `${BASE_URL}/payments/paymentCharges?payments_id=${paymentsId}&user=${user}&type=${currentUser.type}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const merged = {
        payments: data.stripeCharges || [],
        cashPayments: data.cashCharges || [],
        checkPayments: data.checkCharges || []
      };
      setBillData((prev) => ({ ...prev, ...merged }));
      return merged;
    } catch (error) {
      logger.error("Error fetching payment charges", error);
      return null;
    }
  };

  /**
   * Loads everything for a selected appointment: payment header id, automatic charges,
   * aggregate amount paid, and recorded payments (for receipts).
   */
  const loadAppointment = async (appt) => {
    setChargesLoading(true);
    try {
      const paymentId = await ensurePaymentRecord(appt);
      setId(paymentId);

      const [visitCharges, paid] = await Promise.all([
        fetchVisitCharges(appt.id),
        fetchAmountPaid(paymentId),
      ]);

      setCharges(visitCharges);
      setBillInsurance(!!visitCharges?.billInsurance);
      setCopayInput(visitCharges?.copay ? String(visitCharges.copay) : '');
      setNotesInput(visitCharges?.notes || '');
      setNotesDirty(false);
      setTotalPaid(paid);
      await fetchPaymentCharges(paymentId);

      if (visitCharges?.lineItems?.some((l) => !l.priceKnown)) {
        toast.warning("Some services have no price on file — please review before collecting.");
      }
    } catch (error) {
      logger.error("Error loading appointment", error);
      toast.error(error?.timeout
        ? "Timed out loading this appointment — check your connection and try again"
        : "Couldn't load this appointment's charges");
    } finally {
      setChargesLoading(false);
    }
  };

  const onChange = (value) => {
    const selected = schedule.find((appt) => appt.id.toString() === value);
    setSelectedAppointment(selected || null);
    setCharges(null);
    setTotalPaid(0);
    setId(null);
    setSelectedLineKeys([]);
    setShowTransactions(false);
    setBillInsurance(false);
    setCopayInput('');
    setNotesInput('');
    setNotesDirty(false);
    if (selected) loadAppointment(selected);
  };

  // Re-fetch the auto charges after a modification (manual add/remove or discount change).
  const reloadCharges = async () => {
    if (!selectedAppointment) return;
    const vc = await fetchVisitCharges(selectedAppointment.id);
    if (vc) setCharges(vc);
  };

  /* ---------- Modify services ---------- */

  const handleAddService = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    if (!addServiceKey) { toast.warning("Select a service to add"); return; }
    try {
      const res = await fetch(`${BASE_URL}/payments/lineOverride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id, action: 'add', service_key: addServiceKey,
          qty: Number(addServiceQty) || 1, user, type: currentUser.type
        }),
      });
      if (res.ok) {
        toast.success("Service added");
        setAddServiceKey('');
        setAddServiceQty(1);
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to add service");
      }
    } catch (error) {
      logger.error("Error adding service", error);
      toast.error("Failed to add service");
    }
  };

  /* ---------- Open charge (custom amount / set total) ---------- */

  // Add an arbitrary custom line (positive fee or negative ad-hoc discount).
  const handleAddCustomCharge = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    if (!customDesc.trim()) { toast.warning("Enter a description for the custom charge"); return; }
    const amount = Number(customAmount);
    if (!Number.isFinite(amount) || amount === 0) { toast.warning("Enter a non-zero amount"); return; }
    setOpenChargeBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/lineOverride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id, action: 'add', custom: true,
          description: customDesc.trim(), unit_price: amount,
          qty: Number(customQty) || 1, user, type: currentUser.type
        }),
      });
      if (res.ok) {
        toast.success("Custom charge added");
        setCustomDesc('');
        setCustomAmount('');
        setCustomQty(1);
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to add custom charge");
      }
    } catch (error) {
      logger.error("Error adding custom charge", error);
      toast.error("Failed to add custom charge");
    } finally {
      setOpenChargeBusy(false);
    }
  };

  // Replace the bill total with a given figure by adding a single point-in-time adjustment
  // line equal to (target − current total). Itemizes on the receipt and freezes with the bill.
  const handleSetTotal = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    const target = Number(setTotalTarget);
    if (!Number.isFinite(target) || target < 0) { toast.warning("Enter a valid target total"); return; }
    const current = Number(charges?.total || 0);
    const delta = Number((target - current).toFixed(2));
    if (delta === 0) { toast.info("The bill total already equals that amount"); return; }
    setOpenChargeBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/lineOverride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id, action: 'add', custom: true,
          description: `Adjustment (set total to ${money(target)})`,
          unit_price: delta, qty: 1, user, type: currentUser.type
        }),
      });
      if (res.ok) {
        toast.success(`Total adjusted to ${money(target)}`);
        setSetTotalTarget('');
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to adjust total");
      }
    } catch (error) {
      logger.error("Error adjusting total", error);
      toast.error("Failed to adjust total");
    } finally {
      setOpenChargeBusy(false);
    }
  };

  /* ---------- Manage services (catalog admin) ---------- */

  const openManageServices = async () => {
    setManageOpen(true);
    setManageLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/payments/serviceCatalog?includeInactive=true&user=${user}&type=${currentUser.type}`
      );
      if (res.ok) {
        setManageRows(await res.json());
      } else {
        toast.error("Failed to load services");
      }
    } catch (error) {
      logger.error("Error loading services", error);
      toast.error("Failed to load services");
    } finally {
      setManageLoading(false);
    }
  };

  const refreshManageRows = async () => {
    const res = await fetch(
      `${BASE_URL}/payments/serviceCatalog?includeInactive=true&user=${user}&type=${currentUser.type}`
    );
    if (res.ok) setManageRows(await res.json());
    await fetchServiceCatalog();
  };

  const handleCreateService = async () => {
    const key = newSvc.service_key.trim();
    const desc = newSvc.description.trim();
    const price = Number(newSvc.unit_price);
    if (!key || !desc) { toast.warning("Service key and description are required"); return; }
    if (!Number.isFinite(price) || price < 0) { toast.warning("Enter a valid price"); return; }
    setNewSvcBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/serviceCatalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_key: key, description: desc, unit_price: price, user, type: currentUser.type }),
      });
      if (res.ok) {
        toast.success("Service created");
        setNewSvc({ service_key: '', description: '', unit_price: '' });
        await refreshManageRows();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to create service");
      }
    } catch (error) {
      logger.error("Error creating service", error);
      toast.error("Failed to create service");
    } finally {
      setNewSvcBusy(false);
    }
  };

  const handleUpdateService = async (svcId, patch) => {
    try {
      const res = await fetch(`${BASE_URL}/payments/serviceCatalog/${svcId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...patch, user, type: currentUser.type }),
      });
      if (res.ok) {
        await refreshManageRows();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to update service");
      }
    } catch (error) {
      logger.error("Error updating service", error);
      toast.error("Failed to update service");
    }
  };

  // Removing a manual add-on just deletes its override; removing an auto line needs a reason.
  const handleRemoveLine = (line) => {
    if (line.source === 'manual_addon') {
      undoOverride(line.overrideId);
    } else {
      setRemoveReason('');
      setRemoveModal({ open: true, line });
    }
  };

  const confirmRemoveAutoLine = async () => {
    const line = removeModal.line;
    if (!line) return;
    if (!removeReason.trim()) { toast.warning("A reason is required to remove a service"); return; }
    try {
      const res = await fetch(`${BASE_URL}/payments/lineOverride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id, action: 'remove', line_key: line.key,
          description: line.description, reason: removeReason.trim(),
          user, type: currentUser.type
        }),
      });
      if (res.ok) {
        toast.success("Service removed from bill");
        setRemoveModal({ open: false, line: null });
        setRemoveReason('');
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to remove service");
      }
    } catch (error) {
      logger.error("Error removing service", error);
      toast.error("Failed to remove service");
    }
  };

  // Undo any override row (re-instate a removed auto line, or delete a manual add-on).
  const undoOverride = async (overrideId) => {
    if (!overrideId) return;
    try {
      const res = await fetch(`${BASE_URL}/payments/removeLineOverride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_id: overrideId, user, type: currentUser.type }),
      });
      if (res.ok) {
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to undo modification");
      }
    } catch (error) {
      logger.error("Error undoing override", error);
      toast.error("Failed to undo modification");
    }
  };

  /* ---------- Discount codes ---------- */

  const toggleLineKey = (key) => {
    setSelectedLineKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleApplyDiscount = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    if (!discountCode.trim()) { toast.warning("Enter a discount code"); return; }
    const scope = selectedLineKeys.length > 0 ? 'selected' : 'all';
    setDiscountBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/applyDiscount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id, code: discountCode.trim(), scope,
          lineKeys: selectedLineKeys, user, type: currentUser.type
        }),
      });
      if (res.ok) {
        toast.success(`Discount applied${scope === 'selected' ? ' to selected services' : ''}`);
        setDiscountCode('');
        setSelectedLineKeys([]);
        await reloadCharges();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to apply discount");
      }
    } catch (error) {
      logger.error("Error applying discount", error);
      toast.error("Failed to apply discount");
    } finally {
      setDiscountBusy(false);
    }
  };

  const handleRemoveDiscount = async (discountId) => {
    try {
      const res = await fetch(`${BASE_URL}/payments/removeDiscount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount_id: discountId, user, type: currentUser.type }),
      });
      if (res.ok) {
        await reloadCharges();
      } else {
        toast.error("Failed to remove discount");
      }
    } catch (error) {
      logger.error("Error removing discount", error);
      toast.error("Failed to remove discount");
    }
  };

  /* ---------- Transaction history ---------- */

  const loadTransactions = async () => {
    if (showTransactions) { setShowTransactions(false); return; }
    setTxLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/payments/transactionHistory?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (res.ok) {
        setTransactions(await res.json());
        setShowTransactions(true);
      } else {
        toast.error("Failed to load transaction history");
      }
    } catch (error) {
      logger.error("Error loading transactions", error);
      toast.error("Failed to load transaction history");
    } finally {
      setTxLoading(false);
    }
  };

  /* ---------- Print actions ---------- */

  const handlePrintBill = () => {
    try {
      generateBill(billData, chargeData);
      toast.success("Bill generated successfully");
    } catch (error) {
      logger.error("Error generating bill:", error);
      toast.error("Failed to generate bill");
    }
  };

  const handlePrintInvoice = async () => {
    try {
      await generateMailingInvoice({
        responsibleName: chargeData.responsibleName,
        dateOfService: chargeData.dateOfService,
        charges: chargeData.charges,
        amountDue: patientResponsible,
        payments: billData.payments,
        cashPayments: billData.cashPayments,
        checkPayments: billData.checkPayments,
        patientAddress,
      });
      toast.success("Invoice generated successfully");
    } catch (error) {
      logger.error("Error generating invoice:", error);
      toast.error("Failed to generate invoice");
    }
  };

  const handleItemizedReceipt = async () => {
    if (!id) {
      toast.warning("No payment record for this appointment");
      return;
    }
    const data = await fetchPaymentCharges(id);
    try {
      generateReceipt({
        payments: data?.payments || billData.payments,
        cashPayments: data?.cashPayments || billData.cashPayments,
        checkPayments: data?.checkPayments || billData.checkPayments,
        charges: chargeData.charges,
        responsibleName: chargeData.responsibleName,
        dateOfService: chargeData.dateOfService,
      });
      toast.success("Receipt generated successfully");
    } catch (error) {
      logger.error("Error generating receipt:", error);
      toast.error("Failed to generate receipt");
    }
  };

  /* ---------- Insurance billing + copay ---------- */

  // Persist the copay on the payment header (best-effort). Returns whether it saved.
  const persistCopay = async () => {
    if (!id) return false;
    try {
      const res = await fetch(`${BASE_URL}/payments/setCopay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payments_id: id, copay_amount: copayAmount, user, type: currentUser.type }),
      });
      return res.ok;
    } catch (error) {
      logger.error("Error saving copay", error);
      return false;
    }
  };

  const handleSaveCopay = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    if (!copayEntered) {
      toast.warning("Enter the copay amount (enter $0.00 if the patient has no copay).");
      return;
    }
    if (!Number.isFinite(Number(copayInput)) || Number(copayInput) < 0) {
      toast.warning("Enter a valid copay amount");
      return;
    }
    setCopaySaving(true);
    const ok = await persistCopay();
    setCopaySaving(false);
    if (ok) toast.success("Copay saved"); else toast.error("Failed to save copay");
  };

  const handleSaveNotes = async () => {
    if (!id) { toast.warning("No payment record for this appointment"); return; }
    setNotesSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/setNotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payments_id: id, notes: notesInput, user, type: currentUser.type }),
      });
      if (res.ok) {
        toast.success("Notes saved");
        setNotesDirty(false);
      } else {
        toast.error("Failed to save notes");
      }
    } catch (error) {
      logger.error("Error saving notes", error);
      toast.error("Failed to save notes");
    } finally {
      setNotesSaving(false);
    }
  };

  // Toggle the appointment between self-pay and insurance (reuses the scheduling endpoint).
  const handleToggleInsurance = async (next) => {
    if (!selectedAppointment) return;
    setInsuranceToggling(true);
    try {
      const res = await fetch(`${BASE_URL}/scheduling/${selectedAppointment.id}/billInsurance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bill_insurance: next, user, type: currentUser.type }),
      });
      if (res.ok) {
        setBillInsurance(next);
        toast.success(next ? "Billing to insurance — patient owes copay only" : "Switched to self-pay");
      } else {
        toast.error("Failed to update billing mode");
      }
    } catch (error) {
      logger.error("Error toggling insurance billing", error);
      toast.error("Failed to update billing mode");
    } finally {
      setInsuranceToggling(false);
    }
  };

  const handlePay = async () => {
    if (!id) {
      toast.warning("No payment record for this appointment");
      return;
    }
    if (billInsurance && !copayEntered) {
      toast.warning("Enter the patient copay (enter $0.00 if there is none) before collecting.");
      return;
    }
    // For insurance visits, lock in the copay so it survives a reload before collecting it.
    if (billInsurance) await persistCopay();
    navigate('/payment', {
      state: {
        totalCost: patientResponsible,
        totalPaid,
        // Authoritative amount to collect now (handles the discrete-billing/credit case).
        balanceDue,
        patients_id: currentPatient.ris_patients_id,
        payment_id: id,
        appointmentId: selectedAppointment.id,
        patientName,
        email,
        // Label the recorded charge so insurance copays are identifiable in records/receipts.
        chargeDescription: billInsurance ? 'Copay' : null,
      }
    });
  };

  /* ---------- Retroactive card payment ---------- */

  const handleAddRetroPayment = async () => {
    if (!id) {
      toast.warning("Please select an appointment first");
      return;
    }
    if (!retroTransactionId.trim()) {
      toast.warning("Please enter a Stripe transaction ID");
      return;
    }
    if (!retroAmount || Number(retroAmount) <= 0) {
      toast.warning("Please enter a valid payment amount");
      return;
    }
    if (!retroDate) {
      toast.warning("Please enter the date of payment");
      return;
    }

    setRetroLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/payments/addRetroCharge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments_id: id,
          stripe_charge_id: retroTransactionId.trim(),
          amount: Number(retroAmount),
          date: retroDate,
          description: retroDescription.trim() || 'Retroactive card payment',
          user,
          type: currentUser.type
        }),
      });

      if (response.ok) {
        toast.success("Payment record added successfully");
        setRetroModalOpen(false);
        setRetroTransactionId('');
        setRetroAmount('');
        setRetroDate(new Date().toISOString().split('T')[0]);
        setRetroDescription('');
        await fetchPaymentCharges(id);
        setTotalPaid(await fetchAmountPaid(id));
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.message || "Failed to add payment record");
      }
    } catch (error) {
      logger.error("Error adding retroactive payment:", error);
      toast.error("Error adding payment record. Please try again.");
    } finally {
      setRetroLoading(false);
    }
  };

  /* ---------- Group printing ---------- */

  const MAX_GROUP = 10;

  const toggleGroupAppt = (apptId) => {
    setSelectedGroupAppts((prev) => {
      if (prev.includes(apptId)) return prev.filter((x) => x !== apptId);
      if (prev.length >= MAX_GROUP) {
        toast.info(`You can group at most ${MAX_GROUP} appointments in one charge`);
        return prev;
      }
      return [...prev, apptId];
    });
  };

  const selectAllGroupAppts = () => {
    const cap = Math.min(schedule.length, MAX_GROUP);
    if (selectedGroupAppts.length >= cap) {
      setSelectedGroupAppts([]);
    } else {
      if (schedule.length > MAX_GROUP) {
        toast.info(`Selecting the first ${MAX_GROUP} appointments (max per charge)`);
      }
      setSelectedGroupAppts(schedule.slice(0, MAX_GROUP).map((a) => a.id));
    }
  };

  // Busy state is owned by runGroupAction (below), not here — this is shared by three of the
  // four group actions, and each needs to show progress on its own button.
  const fetchGroupedData = async () => {
    try {
      const results = await Promise.all(
        selectedGroupAppts.map(async (apptId) => {
          const [vc, pid] = await Promise.all([
            fetchVisitCharges(apptId),
            getExistingPaymentId(apptId),
          ]);
          const pc = pid
            ? await fetchWithTimeout(`${BASE_URL}/payments/paymentCharges?payments_id=${pid}&user=${user}&type=${currentUser.type}`).then((r) => r.json()).catch(() => null)
            : null;
          return { apptId, vc, pc };
        })
      );

      const groupedCharges = [];
      for (const { apptId, vc } of results) {
        if (!vc) continue;
        const date = (vc.dateOfService || schedule.find((a) => a.id === apptId)?.appointment_start || '').split('T')[0] || 'N/A';
        (vc.lineItems || []).filter((l) => l.qty >= 1).forEach((l) => {
          groupedCharges.push({
            description: l.description, qty: l.qty, unitPrice: l.unitPrice, date,
            cptCode: l.cptCode || null, orderingProvider: l.orderingProvider || null, referringProvider: l.referringProvider || null,
          });
        });
        (vc.discounts || []).forEach((d) => {
          groupedCharges.push({ description: d.description, qty: 1, unitPrice: d.amount, date });
        });
        // Insurance visits net to the patient copay (services are billed to the payer).
        if (vc.billInsurance) {
          const adjustment = Number(((Number(vc.copay) || 0) - Number(vc.total || 0)).toFixed(2));
          if (adjustment !== 0) {
            groupedCharges.push({ description: 'Less: Billed to insurance (patient owes copay)', qty: 1, unitPrice: adjustment, date });
          }
        }
      }

      return {
        charges: groupedCharges,
        payments: results.flatMap((r) => r.pc?.stripeCharges || []),
        cashPayments: results.flatMap((r) => r.pc?.cashCharges || []),
        checkPayments: results.flatMap((r) => r.pc?.checkCharges || []),
        responsibleName: patientName,
        dateOfService: null,
      };
    } catch (error) {
      logger.error("Error fetching grouped data", error);
      toast.error(error?.timeout
        ? "Timed out loading grouped payment data — check your connection and try again"
        : "Error loading grouped payment data");
      return null;
    }
  };

  /**
   * Runs one group action with its own busy key, so only the button that was clicked shows
   * progress. The `finally` is the important part: whatever the action does — throws, returns
   * early, fails a fetch — the button always comes back. Previously a request that never
   * settled left all four buttons reading "Loading…" with no way out but a page refresh.
   */
  const runGroupAction = async (key, action) => {
    if (groupBusy) return;             // ignore clicks while another action is running
    setGroupBusy(key);
    try {
      await action();
    } catch (error) {
      logger.error(`Group action "${key}" failed`, error);
      toast.error(error?.timeout
        ? "That took too long and was cancelled — check your connection and try again"
        : "Something went wrong. Please try again.");
    } finally {
      setGroupBusy(null);
    }
  };

  const handleGroupBill = () => runGroupAction('bill', async () => {
    const data = await fetchGroupedData();
    if (data) {
      generateBill(
        { payments: data.payments, cashPayments: data.cashPayments, checkPayments: data.checkPayments },
        { responsibleName: data.responsibleName, dateOfService: data.dateOfService, charges: data.charges }
      );
    }
  });

  const handleGroupReceipt = () => runGroupAction('receipt', async () => {
    const data = await fetchGroupedData();
    if (data) {
      generateReceipt({
        payments: data.payments,
        cashPayments: data.cashPayments,
        checkPayments: data.checkPayments,
        charges: data.charges,
        responsibleName: data.responsibleName,
        dateOfService: data.dateOfService,
      });
    }
  });

  // Combined collection: settle several appointments in one transaction. Builds the
  // per-appointment allocation (each appointment's outstanding balance) and hands off
  // to the payment screen with a shared group_ref.
  const handleCollectTogether = () => runGroupAction('collect', async () => {
    if (selectedGroupAppts.length === 0) return;

    const allocations = [];
    let combinedTotal = 0;
    const unavailable = [];   // appointments we couldn't open a payment record for

    for (const apptId of selectedGroupAppts) {
      const appt = schedule.find((a) => a.id === apptId);
      if (!appt) continue;
      const pid = await ensurePaymentRecord(appt);
      if (!pid) {
        // ensurePaymentRecord has already explained why. Track it so we don't mislabel a
        // backend failure as "no balance due".
        unavailable.push(apptId);
        continue;
      }
      const [vc, paid] = await Promise.all([fetchVisitCharges(apptId), fetchAmountPaid(pid)]);
      const balance = computeBalanceDue(vc, paid);
      if (balance > 0) {
        allocations.push({ payment_id: pid, schedule_id: apptId, amount: Math.round(balance * 100) });
        combinedTotal += balance;
      }
    }

    if (allocations.length === 0) {
      if (unavailable.length > 0) {
        toast.error(
          `Couldn't open a payment record for appointment${unavailable.length > 1 ? 's' : ''} ` +
          `#${unavailable.join(', #')}. Nothing was collected.`
        );
      } else {
        toast.info("Selected appointments have no balance due");
      }
      return;
    }
    if (unavailable.length > 0) {
      toast.warning(
        `Appointment${unavailable.length > 1 ? 's' : ''} #${unavailable.join(', #')} could not be ` +
        `included — continuing with the other ${allocations.length}.`
      );
    }

    const group_ref = `grp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    navigate('/payment', {
      state: {
        group: {
          allocations,
          group_ref,
          combinedTotal: Number(combinedTotal.toFixed(2)),
          patients_id: currentPatient.ris_patients_id,
          patientName,
          email,
        }
      }
    });
  });

  const handleGroupInvoice = () => runGroupAction('invoice', async () => {
    const data = await fetchGroupedData();
    if (data) {
      const amountDue = data.charges.reduce((sum, c) => sum + c.qty * c.unitPrice, 0);
      generateMailingInvoice({
        responsibleName: patientName,
        patientAddress,
        dateOfService: data.dateOfService,
        charges: data.charges,
        amountDue,
        payments: data.payments,
        cashPayments: data.cashPayments,
        checkPayments: data.checkPayments,
      });
    }
  });

  /* ---------- Effects ---------- */

  useEffect(() => {
    fetchAppointments();
    fetchPatientAddress();
    fetchServiceCatalog();
    fetchInsuranceOnFile();
  }, []);

  // Auto-select the appointment handed off from the Scheduling checkout flow.
  useEffect(() => {
    if (preselectAppointmentId && schedule.length > 0 && !selectedAppointment) {
      const match = schedule.find((appt) => appt.id.toString() === preselectAppointmentId.toString());
      if (match) {
        setSelectedAppointment(match);
        loadAppointment(match);
      }
    }
  }, [schedule]);

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="hgi-payments-page">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <PageTopBar links={[
        { label: 'Home', to: '/home', icon: Home },
        { label: 'Check-In', to: '/checkIn', icon: UserCheck },
      ]} />
      <div className="payments-container">
        <div className="header-payments-row">
          <div className="hg-head-titles">
            <div className="hg-head-icon"><Wallet size={22} strokeWidth={2} /></div>
            <div>
              <h3>Payments</h3>
              <span className="hg-head-sub">{patientName || 'No patient selected'}</span>
            </div>
          </div>
          <div className="payments-items-row">
            <select className='appointment-selector-payments' value={selectedAppointment?.id || ''} onChange={(e) => onChange(e.target.value)}>
              <option value="">Select from Schedule</option>
              {schedule.map((appt) => (
                <option key={appt.id} value={appt.id}>
                  {appt.id} | {appt.first_name + ' ' + appt.last_name} | {appt.exam} | {formatAppointmentDate(appt.appointment_start)} {formatAppointmentTimeRange(appt.appointment_start, appt.appointment_end)}
                </option>
              ))}
            </select>
            <button className="PrimaryButton" onClick={() => setGroupModalOpen(true)} disabled={schedule.length === 0}>
              Group
            </button>
          </div>
        </div>

        {groupModalOpen && (
          <div className="group-modal-overlay" onClick={() => setGroupModalOpen(false)}>
            <div className="group-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Select Appointments to Group</h3>
              <div className="group-modal-header">
                <button className="PrimaryButton" onClick={selectAllGroupAppts}>
                  {selectedGroupAppts.length === schedule.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="group-modal-count">{selectedGroupAppts.length} selected</span>
              </div>
              <div className="group-modal-list">
                {schedule.map((appt) => (
                  <label key={appt.id} className="group-modal-item">
                    <input
                      type="checkbox"
                      checked={selectedGroupAppts.includes(appt.id)}
                      onChange={() => toggleGroupAppt(appt.id)}
                    />
                    <span>{appt.id} | {appt.first_name + ' ' + appt.last_name} | {appt.exam} | {formatAppointmentDate(appt.appointment_start)} {formatAppointmentTimeRange(appt.appointment_start, appt.appointment_end)}</span>
                  </label>
                ))}
              </div>
              {selectedGroupAppts.length > 0 && (
                <div className="group-modal-actions">
                  <button className="PrimaryButton" onClick={handleCollectTogether} disabled={groupLoading}>
                    {groupBusy === 'collect' ? 'Preparing…' : 'Collect Payment Together'}
                  </button>
                  <button className="PrimaryButton" onClick={handleGroupBill} disabled={groupLoading}>
                    {groupBusy === 'bill' ? 'Building…' : 'Print Bill'}
                  </button>
                  <button className="PrimaryButton" onClick={handleGroupReceipt} disabled={groupLoading}>
                    {groupBusy === 'receipt' ? 'Building…' : 'Itemized Receipt'}
                  </button>
                  <button className="PrimaryButton" onClick={handleGroupInvoice} disabled={groupLoading}>
                    {groupBusy === 'invoice' ? 'Building…' : 'Print Invoice'}
                  </button>
                  <button className="group-modal-cancel-btn" onClick={() => setGroupModalOpen(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedAppointment && (
          <div className="hg-dos">
            <span className="hg-dos-label">Date of Service</span>
            <span className="hg-dos-value">{formatAppointmentDate(selectedAppointment.appointment_start)} {formatAppointmentTimeRange(selectedAppointment.appointment_start, selectedAppointment.appointment_end)}</span>
          </div>
        )}

        {selectedAppointment ? (
          <div className="payments-list">
            {chargesLoading && <p>Loading charges…</p>}

            {!chargesLoading && charges && (
              <>
                {/* Billing mode: self-pay vs insurance + copay entry */}
                <div className={`hg-card hg-billing-mode ${billInsurance ? 'is-insurance' : ''}`}>
                  <div className="hg-billing-head">
                    <span className="hg-billing-title">Billing</span>
                    <div className="hg-segmented hg-billing-toggle">
                      <button
                        className={!billInsurance ? 'active' : ''}
                        onClick={() => { if (billInsurance) handleToggleInsurance(false); }}
                        disabled={insuranceToggling}
                      >Self-Pay</button>
                      <button
                        className={billInsurance ? 'active' : ''}
                        onClick={() => { if (!billInsurance) handleToggleInsurance(true); }}
                        disabled={insuranceToggling}
                      >Insurance</button>
                    </div>
                  </div>
                  {billInsurance && activeInsuranceCount === 0 && (
                    <div className="hg-insurance-warning" style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                      marginTop: 10, backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 8, color: '#991b1b',
                    }}>
                      <ShieldAlert size={20} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>No insurance on record</div>
                        <p style={{ margin: '4px 0 8px', fontSize: 13 }}>
                          This visit is set to bill insurance, but this patient has no insurance information on
                          file. The payment can't be billed as insurance until a policy is added.
                        </p>
                        <button
                          className="PrimaryButton"
                          onClick={() => setShowAddInsuranceModal(true)}
                          disabled={!currentPatient?.ris_patients_id}
                        >
                          Add Insurance
                        </button>
                      </div>
                    </div>
                  )}
                  {billInsurance && (
                    <div className="hg-copay-row">
                      <span className="hg-copay-note">
                        Insurance is billed for the services below. Collect only the patient copay.
                      </span>
                      <div className="hg-copay-input-group">
                        <label className="hg-control-label">Copay <span style={{ color: '#ef4444' }}>*</span></label>
                        <NumericFormat
                          value={copayInput}
                          onValueChange={({ floatValue }) => setCopayInput(floatValue ?? '')}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          thousandSeparator={true}
                          prefix="$"
                          allowNegative={false}
                          placeholder="Enter $0.00 if none"
                          className="hg-input hg-code"
                          style={copayRequiredMissing ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : undefined}
                        />
                        <button className="PrimaryButton" onClick={handleSaveCopay} disabled={copaySaving || !id}>
                          {copaySaving ? 'Saving…' : 'Save copay'}
                        </button>
                      </div>
                      {copayRequiredMissing && (
                        <span className="hg-copay-required" style={{ color: '#b91c1c', fontSize: 13, fontWeight: 500 }}>
                          A copay amount is required for insurance billing. Enter $0.00 if the patient has no copay.
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="hg-card hg-notes">
                  <label className="hg-control-label" htmlFor="hg-payment-notes">Payment Notes</label>
                  <textarea
                    id="hg-payment-notes"
                    className="hg-input hg-notes-textarea"
                    style={{ width: '100%', minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                    placeholder="e.g. Patient set up on a payment plan, Free DEXA Coupon applied, etc."
                    value={notesInput}
                    onChange={(e) => { setNotesInput(e.target.value); setNotesDirty(true); }}
                  />
                  <button
                    className="PrimaryButton"
                    onClick={handleSaveNotes}
                    disabled={notesSaving || !id || !notesDirty}
                    style={{ marginTop: 8 }}
                  >
                    {notesSaving ? 'Saving…' : 'Save Notes'}
                  </button>
                </div>

                {charges.membership?.active && (
                  <span className="hg-pill hg-pill-green">
                    ✓ Membership — {Math.round((charges.membership.rate || 0) * 100)}% imaging discount applied
                  </span>
                )}

                <div className="hg-card">
                  <table className="hg-table hg-service-table">
                    <thead>
                      <tr>
                        <th className="hg-col-check"></th>
                        <th>Description</th>
                        <th className="hg-num">Qty</th>
                        <th className="hg-num">Unit Price</th>
                        <th className="hg-num">Amount</th>
                        <th className="hg-col-action"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(charges.lineItems || []).map((l) => (
                        <tr key={l.key}>
                          <td className="hg-col-check">
                            <input
                              type="checkbox"
                              title="Select for a line-specific discount"
                              checked={selectedLineKeys.includes(l.key)}
                              onChange={() => toggleLineKey(l.key)}
                              disabled={l.unitPrice <= 0}
                            />
                          </td>
                          <td>
                            {l.description}
                            {l.source === 'manual_addon' && <span className="hg-muted"> (added)</span>}
                            {!l.priceKnown && <span className="hg-warn"> ⚠ no price on file</span>}
                          </td>
                          <td className="hg-num">{l.qty}</td>
                          <td className="hg-num">{money(l.unitPrice)}</td>
                          <td className="hg-num">{money(l.qty * l.unitPrice)}</td>
                          <td className="hg-col-action">
                            {l.removable && (
                              <button className="hg-icon-btn hg-icon-danger" title="Remove from bill" onClick={() => handleRemoveLine(l)}>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(charges.discounts || []).map((d, i) => (
                        <tr key={`disc-${d.id ?? 'm'}-${i}`} className="hg-discount-row">
                          <td></td>
                          <td>
                            {d.description}
                            {d.scope === 'selected' && <span className="hg-muted"> (selected lines)</span>}
                          </td>
                          <td className="hg-num">1</td>
                          <td className="hg-num">{money(d.amount)}</td>
                          <td className="hg-num">{money(d.amount)}</td>
                          <td className="hg-col-action">
                            {d.removable && d.id && (
                              <button className="hg-icon-btn hg-icon-danger" title="Remove discount" onClick={() => handleRemoveDiscount(d.id)}>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(charges.lineItems || []).length === 0 && (
                        <tr><td colSpan={6} className="hg-empty">No billable services found for this appointment.</td></tr>
                      )}
                    </tbody>
                  </table>

                  {(charges.removedLines || []).length > 0 && (
                    <div className="hg-removed">
                      <span className="hg-removed-title">Removed from bill</span>
                      {(charges.removedLines || []).map((r) => (
                        <div key={r.overrideId} className="hg-removed-item">
                          <span className="hg-strike">{r.description} — {money(r.unitPrice)}</span>
                          {r.reason && <span className="hg-muted"> · Reason: {r.reason}</span>}
                          <button className="hg-link-btn" onClick={() => undoOverride(r.overrideId)}>Restore</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Modify services + discount */}
                <div className="hg-card hg-controls">
                  <div className="hg-control-row">
                    <label className="hg-control-label">Add service</label>
                    <select value={addServiceKey} onChange={(e) => setAddServiceKey(e.target.value)} className="hg-input hg-select">
                      <option value="">Select a service…</option>
                      {serviceCatalog.map((s) => (
                        <option key={s.id} value={s.service_key}>{s.description} — {money(s.unit_price)}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={addServiceQty}
                      onChange={(e) => setAddServiceQty(e.target.value)}
                      className="hg-input hg-qty"
                    />
                    <button className="PrimaryButton" onClick={handleAddService} disabled={!id || !addServiceKey}>Add</button>
                    <button className="hg-link-btn" onClick={openManageServices} title="Add or edit services and prices">Manage services</button>
                  </div>

                  <div className="hg-control-row">
                    <label className="hg-control-label">Open charge</label>
                    <input
                      type="text"
                      value={customDesc}
                      onChange={(e) => setCustomDesc(e.target.value)}
                      placeholder="Description (e.g. Courtesy adjustment)"
                      className="hg-input hg-open-desc"
                    />
                    <NumericFormat
                      value={customAmount}
                      onValueChange={({ floatValue }) => setCustomAmount(floatValue ?? '')}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      thousandSeparator={true}
                      prefix="$"
                      allowNegative={true}
                      placeholder="$0.00"
                      className="hg-input hg-code"
                    />
                    <input
                      type="number"
                      min="1"
                      value={customQty}
                      onChange={(e) => setCustomQty(e.target.value)}
                      className="hg-input hg-qty"
                    />
                    <button className="PrimaryButton" onClick={handleAddCustomCharge} disabled={openChargeBusy || !id || !customDesc.trim()}>Add charge</button>
                    <span className="hg-hint">Use a negative amount for an ad-hoc discount</span>
                  </div>

                  <div className="hg-control-row">
                    <label className="hg-control-label">Set total to</label>
                    <NumericFormat
                      value={setTotalTarget}
                      onValueChange={({ floatValue }) => setSetTotalTarget(floatValue ?? '')}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      thousandSeparator={true}
                      prefix="$"
                      allowNegative={false}
                      placeholder="$0.00"
                      className="hg-input hg-code"
                    />
                    <button className="PrimaryButton" onClick={handleSetTotal} disabled={openChargeBusy || !id || setTotalTarget === ''}>Set total</button>
                    <span className="hg-hint">Adds a one-time adjustment line so the bill totals this amount</span>
                  </div>

                  <div className="hg-control-row">
                    <label className="hg-control-label">Discount code</label>
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      placeholder="e.g. OVERRIDE"
                      className="hg-input hg-code"
                    />
                    <button className="PrimaryButton" onClick={handleApplyDiscount} disabled={discountBusy || !id || !discountCode.trim()}>
                      {discountBusy ? 'Applying…' : 'Apply'}
                    </button>
                    <span className="hg-hint">
                      {selectedLineKeys.length > 0
                        ? `Applies to ${selectedLineKeys.length} selected line(s)`
                        : 'Applies to all lines (check boxes to target specific services)'}
                    </span>
                  </div>

                  <div className="hg-code-notice">
                    <span className="hg-code-notice-title">Common discount codes</span>
                    <div className="hg-code-notice-list">
                      <button
                        type="button"
                        className="hg-code-chip"
                        title="Click to use this code"
                        onClick={() => setDiscountCode('MEMBERSHIP')}
                      >
                        <code>MEMBERSHIP</code>
                        <span className="hg-code-chip-desc">10% member discount</span>
                      </button>
                      <button
                        type="button"
                        className="hg-code-chip"
                        title="Click to use this code"
                        onClick={() => setDiscountCode('OVERRIDE')}
                      >
                        <code>OVERRIDE</code>
                        <span className="hg-code-chip-desc">100% override on charge(s)</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Totals summary */}
                <div className="hg-summary">
                  <div className="hg-summary-item">
                    <span className="hg-summary-label">Subtotal</span>
                    <span className="hg-summary-value">{money(charges.subtotal)}</span>
                  </div>
                  {charges.discountTotal !== 0 && (
                    <div className="hg-summary-item">
                      <span className="hg-summary-label">Discounts</span>
                      <span className="hg-summary-value hg-green">{money(charges.discountTotal)}</span>
                    </div>
                  )}
                  {billInsurance ? (
                    <>
                      <div className="hg-summary-item">
                        <span className="hg-summary-label">Billed to Insurance</span>
                        <span className="hg-summary-value">{money(charges.total)}</span>
                      </div>
                      <div className="hg-summary-item">
                        <span className="hg-summary-label">Copay (Patient Owes)</span>
                        <span className="hg-summary-value" style={copayRequiredMissing ? { color: '#b91c1c' } : undefined}>
                          {copayRequiredMissing ? 'Required' : money(copayAmount)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="hg-summary-item">
                      <span className="hg-summary-label">Total Owed</span>
                      <span className="hg-summary-value">{money(charges.total)}</span>
                    </div>
                  )}
                  <div className="hg-summary-item">
                    <span className="hg-summary-label">Total Paid</span>
                    <span className="hg-summary-value">{money(totalPaid)}</span>
                  </div>
                  <div className={`hg-summary-item hg-summary-balance ${!copayRequiredMissing && balanceDue <= 0 ? 'paid' : ''}`}>
                    <span className="hg-summary-label">Balance Due</span>
                    <span className="hg-summary-value">{copayRequiredMissing ? 'Enter copay' : money(balanceDue)}</span>
                  </div>
                </div>

                <div className="hg-actions">
                  {id && copayRequiredMissing && (
                    <button
                      className="PrimaryButton hg-pay-btn"
                      disabled
                      title="Enter the patient copay (enter $0.00 if there is none) before collecting"
                    >
                      Pay
                    </button>
                  )}
                  {id && !copayRequiredMissing && balanceDue > 0 && (
                    <button className="PrimaryButton hg-pay-btn" onClick={handlePay}>Pay</button>
                  )}
                  <button className="hg-btn-secondary" onClick={handlePrintBill}>Print Bill</button>
                  <button className="hg-btn-secondary" onClick={handlePrintInvoice}>Print Invoice</button>
                  <button className="hg-btn-secondary" onClick={handleItemizedReceipt}>Itemized Receipt</button>
                  {id && (
                    <button className="hg-btn-secondary" onClick={() => setRetroModalOpen(true)}>Add Card Payment</button>
                  )}
                  <button className="hg-btn-secondary" onClick={loadTransactions} disabled={txLoading}>
                    {txLoading ? 'Loading…' : (showTransactions ? 'Hide Transactions' : 'View Transactions')}
                  </button>
                </div>

                {showTransactions && (
                  <div className="hg-card hg-transactions">
                    <div className="hg-transactions-head">
                      <h4>Past Transactions</h4>
                      <div className="hg-segmented">
                        <button
                          className={txScope === 'appointment' ? 'active' : ''}
                          onClick={() => setTxScope('appointment')}
                        >This Appointment</button>
                        <button
                          className={txScope === 'all' ? 'active' : ''}
                          onClick={() => setTxScope('all')}
                        >All for Patient</button>
                      </div>
                    </div>
                    {visibleTransactions.length === 0 ? (
                      <p className="hg-empty">
                        {txScope === 'appointment' ? 'No payments recorded for this appointment.' : 'No recorded payments for this patient.'}
                      </p>
                    ) : (
                      <table className="hg-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Method</th>
                            <th>Description</th>
                            <th className="hg-num">Applied to Bill</th>
                            <th className="hg-num">Processing Fee</th>
                            <th className="hg-num">Total Charged</th>
                            <th>Reference</th>
                            <th>Receipt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleTransactions.map((t, i) => (
                            <tr key={i}>
                              <td>{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                              <td>{t.method}{t.group_ref ? <span className="hg-muted"> (grouped)</span> : ''}</td>
                              <td>{t.description || '—'}</td>
                              <td className="hg-num">{money(t.amount)}</td>
                              <td className="hg-num">{t.surcharge > 0 ? money(t.surcharge) : '—'}</td>
                              <td className="hg-num">{money(t.totalCharged != null ? t.totalCharged : t.amount)}</td>
                              <td className="hg-ref">{t.reference || '—'}</td>
                              <td>
                                {t.receipt_url
                                  ? <a href={t.receipt_url} target="_blank" rel="noopener noreferrer">View</a>
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="hg-empty-state">
            <div className="hg-empty-hero">
              <div className="hg-empty-icon"><ReceiptText size={30} strokeWidth={1.8} /></div>
              <h2>{patientName ? 'Select an appointment to begin' : 'No patient selected'}</h2>
              <p>
                {patientName
                  ? 'Choose a visit below to review its charges, collect payment, and print receipts.'
                  : 'Pick a patient from the Check-In screen to start processing their payments.'}
              </p>
              {!patientName && (
                <button className="hg-empty-cta" onClick={() => navigate('/checkIn')}>
                  <UserPlus size={16} /> Go to Check-In
                </button>
              )}
            </div>

            {schedule.length > 0 && (
              <div className="hg-appt-picker">
                <div className="hg-appt-picker-head">
                  <span className="hg-appt-picker-title">Appointments</span>
                  <span className="hg-appt-picker-count">{schedule.length}</span>
                </div>
                <div className="hg-appt-grid">
                  {schedule.map((appt, idx) => (
                    <button
                      key={appt.id}
                      className="hg-appt-card"
                      style={{ animationDelay: `${Math.min(idx * 45, 360)}ms` }}
                      onClick={() => onChange(appt.id.toString())}
                    >
                      <div className="hg-appt-card-top">
                        <span className="hg-appt-exam">{appt.exam}</span>
                        <span className="hg-appt-visit">{appt.visit_type}</span>
                      </div>
                      <div className="hg-appt-card-name">{appt.first_name} {appt.last_name}</div>
                      <div className="hg-appt-card-date">
                        <CalendarClock size={14} />
                        <span>{formatAppointmentDate(appt.appointment_start)} · {formatAppointmentTimeRange(appt.appointment_start, appt.appointment_end)}</span>
                      </div>
                      <div className="hg-appt-card-go">
                        Collect payment <ArrowRight size={15} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {removeModal.open && (
        <div className="group-modal-overlay" onClick={() => setRemoveModal({ open: false, line: null })}>
          <div className="group-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remove Service from Bill</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
              Removing <strong>{removeModal.line?.description}</strong> ({money(removeModal.line?.unitPrice)}) takes it off the bill.
              The underlying order is <strong>not</strong> deleted. A reason is required.
            </p>
            <div className="retro-payment-field">
              <label>Reason *</label>
              <input
                type="text"
                className="copayAmountInput"
                style={{ width: '100%' }}
                placeholder="e.g. Exam not performed / billed under another visit"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
              />
            </div>
            <div className="group-modal-actions">
              <button className="PrimaryButton" onClick={confirmRemoveAutoLine}>Remove Service</button>
              <button className="group-modal-cancel-btn" onClick={() => setRemoveModal({ open: false, line: null })}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {retroModalOpen && (
        <div className="group-modal-overlay" onClick={() => setRetroModalOpen(false)}>
          <div className="group-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Retroactive Card Payment</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
              Record a Stripe payment that was collected outside the system (e.g. after mailing an invoice).
            </p>

            <div className="retro-payment-form">
              <div className="retro-payment-field">
                <label>Stripe Transaction ID <span style={{ color: '#999', fontWeight: 'normal' }}>(ch_... or pi_...)</span></label>
                <input
                  type="text"
                  className="copayAmountInput"
                  style={{ width: '100%', fontFamily: 'monospace' }}
                  placeholder="ch_3abc... or pi_3abc..."
                  value={retroTransactionId}
                  onChange={(e) => setRetroTransactionId(e.target.value)}
                />
              </div>

              <div className="retro-payment-field">
                <label>Amount Paid</label>
                <NumericFormat
                  value={retroAmount}
                  onValueChange={({ floatValue }) => setRetroAmount(floatValue ?? '')}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  thousandSeparator={true}
                  prefix="$"
                  className="copayAmountInput"
                  style={{ width: '100%' }}
                  placeholder="$0.00"
                />
              </div>

              <div className="retro-payment-field">
                <label>Date of Payment</label>
                <input
                  type="date"
                  className="copayAmountInput"
                  style={{ width: '100%' }}
                  value={retroDate}
                  onChange={(e) => setRetroDate(e.target.value)}
                />
              </div>

              <div className="retro-payment-field">
                <label>Description <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
                <input
                  type="text"
                  className="copayAmountInput"
                  style={{ width: '100%' }}
                  placeholder="e.g. Payment received via mailed invoice"
                  value={retroDescription}
                  onChange={(e) => setRetroDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="group-modal-actions">
              <button className="PrimaryButton" onClick={handleAddRetroPayment} disabled={retroLoading}>
                {retroLoading ? 'Saving...' : 'Save Payment'}
              </button>
              <button className="group-modal-cancel-btn" onClick={() => setRetroModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddInsuranceModal && (
        <InsuranceCaptureModal
          risPatientsId={currentPatient.ris_patients_id}
          user={user}
          type={currentUser.type}
          onClose={() => setShowAddInsuranceModal(false)}
          onSaved={async () => {
            await fetchInsuranceOnFile();
            toast.success('Insurance added to patient record');
          }}
        />
      )}

      {manageOpen && (
        <div className="group-modal-overlay" onClick={() => setManageOpen(false)}>
          <div className="group-modal hg-manage-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Services</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
              Add-on services and prices used by the “Add service” control. Changes take effect
              immediately — no redeploy. Editing a price does not change bills already created.
            </p>

            {manageLoading ? (
              <p>Loading services…</p>
            ) : (
              <table className="hg-table hg-manage-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th className="hg-num">Price</th>
                    <th className="hg-num">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {manageRows.map((s) => (
                    <tr key={s.id} className={s.active ? '' : 'hg-inactive-row'}>
                      <td><code>{s.service_key}</code></td>
                      <td>
                        <input
                          type="text"
                          className="hg-input"
                          style={{ width: '100%' }}
                          defaultValue={s.description}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== s.description) handleUpdateService(s.id, { description: v });
                          }}
                        />
                      </td>
                      <td className="hg-num">
                        <NumericFormat
                          defaultValue={Number(s.unit_price)}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          thousandSeparator={true}
                          prefix="$"
                          allowNegative={false}
                          className="hg-input hg-qty"
                          style={{ width: '90px', textAlign: 'right' }}
                          onBlur={(e) => {
                            const v = Number(String(e.target.value).replace(/[^0-9.-]/g, ''));
                            if (Number.isFinite(v) && v !== Number(s.unit_price)) handleUpdateService(s.id, { unit_price: v });
                          }}
                        />
                      </td>
                      <td className="hg-num">
                        <input
                          type="checkbox"
                          checked={!!s.active}
                          onChange={(e) => handleUpdateService(s.id, { active: e.target.checked })}
                        />
                      </td>
                    </tr>
                  ))}
                  {manageRows.length === 0 && (
                    <tr><td colSpan={4} className="hg-empty">No services yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}

            <div className="hg-manage-new">
              <span className="hg-code-notice-title">Add a new service</span>
              <div className="hg-control-row">
                <input
                  type="text"
                  className="hg-input"
                  placeholder="key (e.g. echo)"
                  value={newSvc.service_key}
                  onChange={(e) => setNewSvc((p) => ({ ...p, service_key: e.target.value }))}
                />
                <input
                  type="text"
                  className="hg-input hg-open-desc"
                  placeholder="Description"
                  value={newSvc.description}
                  onChange={(e) => setNewSvc((p) => ({ ...p, description: e.target.value }))}
                />
                <NumericFormat
                  value={newSvc.unit_price}
                  onValueChange={({ floatValue }) => setNewSvc((p) => ({ ...p, unit_price: floatValue ?? '' }))}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  thousandSeparator={true}
                  prefix="$"
                  allowNegative={false}
                  placeholder="$0.00"
                  className="hg-input hg-code"
                />
                <button className="PrimaryButton" onClick={handleCreateService} disabled={newSvcBusy}>
                  {newSvcBusy ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>

            <div className="group-modal-actions">
              <button className="group-modal-cancel-btn" onClick={() => setManageOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HGIPayments;
